import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { Loader2, BookOpen, Activity, Layers, Sparkles, HeartPulse, Download, ExternalLink, Archive, Wand2, Undo2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Kpi = ({ label, value, sub, testId, onClick }) => (
  <Card data-testid={testId} onClick={onClick}
    className={onClick ? 'cursor-pointer hover:border-[#4ABFCF] transition-colors' : undefined}>
    <CardContent className="pt-4 pb-3">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-[#0F2040]">{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </CardContent>
  </Card>
);

const Bar = ({ pct, cls = 'bg-[#4ABFCF]' }) => (
  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
    <div className={`h-full rounded-full ${cls}`} style={{ width: `${Math.max(2, Math.min(100, pct || 0))}%` }} />
  </div>
);

const FRESH_META = [
  ['new90', 'Updated <90d', 'bg-emerald-500'],
  ['mid', '3–6 months', 'bg-[#4ABFCF]'],
  ['aging', '6–12 months', 'bg-amber-400'],
  ['stale', 'Stale >12m', 'bg-rose-400'],
];

export const LibraryInsightsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [neverOpen, setNeverOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState('');
  const [recipients, setRecipients] = useState(null);
  const [backups, setBackups] = useState(null);
  const [bkBusy, setBkBusy] = useState('');
  const navigate = useNavigate();

  const loadBackups = () => {
    axios.get(`${API_URL}/api/rfp/library-backups`, { withCredentials: true })
      .then(res => setBackups(res.data.backups || []))
      .catch(() => setBackups(null));
  };

  const backupNow = async () => {
    setBkBusy('now');
    try {
      const res = await axios.post(`${API_URL}/api/rfp/library-backups`, {}, { withCredentials: true });
      toast.success(`Backup saved — ${res.data.block_count} entries snapshotted`);
      loadBackups();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not back up the library'); }
    setBkBusy('');
  };

  const restoreBackup = async (b) => {
    if (!window.confirm(`Restore the library to the ${b.day} snapshot (${b.block_count} entries)? The current state is backed up first, so this is undoable.`)) return;
    setBkBusy(`restore-${b.id}`);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/library-backups/${b.id}/restore`, {}, { withCredentials: true });
      toast.success(`Library restored — ${res.data.restored_blocks} entries from ${res.data.from_day}`);
      loadBackups();
      const d = await axios.get(`${API_URL}/api/rfp/boilerplate/insights/dashboard`, { withCredentials: true });
      setData(d.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
    setBkBusy('');
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/rfp/boilerplate/monthly-report/recipients`, { withCredentials: true })
      .then(res => setRecipients((res.data?.emails || []).join(', ')))
      .catch(() => {});
  }, []);

  const saveRecipients = async () => {
    try {
      const res = await axios.put(`${API_URL}/api/rfp/boilerplate/monthly-report/recipients`,
        { emails: recipients.split(',').map(e => e.trim()).filter(Boolean) }, { withCredentials: true });
      setRecipients((res.data?.emails || []).join(', '));
      toast.success('Monthly report recipients saved');
    } catch { toast.error('Could not save recipients'); }
  };

  const restoreEntry = async (e) => {
    setRowBusy(`restore-${e.capability_id}`);
    try {
      await axios.post(`${API_URL}/api/rfp/boilerplate/${e.id}/unarchive`, {}, { withCredentials: true });
      toast.success(`${e.capability_id} restored to the active library`);
      setData(d => ({
        ...d, total: d.total + 1, archived: d.archived - 1,
        recently_archived: d.recently_archived.filter(x => x.id !== e.id),
      }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not restore the entry'); }
    finally { setRowBusy(''); }
  };

  const boostKeywords = async (e) => {
    setRowBusy(`boost-${e.capability_id}`);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/boilerplate/${e.id}/boost-keywords`, {}, { withCredentials: true });
      toast.success(`Keywords refreshed (${(res.data?.keywords || []).length}): ${(res.data?.keywords || []).slice(0, 5).join(', ')}…`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not boost keywords'); }
    finally { setRowBusy(''); }
  };

  const retireEntry = async (e) => {
    setRowBusy(`retire-${e.capability_id}`);
    try {
      await axios.post(`${API_URL}/api/rfp/boilerplate/${e.id}/archive`, {}, { withCredentials: true });
      toast.success(`${e.capability_id} retired (archived — reversible from the library)`);
      setData(d => ({
        ...d, never_used: d.never_used - 1, total: d.total - 1, archived: d.archived + 1,
        never_used_entries: d.never_used_entries.filter(x => x.capability_id !== e.capability_id),
      }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not retire the entry'); }
    finally { setRowBusy(''); }
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/rfp/boilerplate/insights/dashboard/export/pdf`,
        { withCredentials: true, responseType: 'blob' });
      saveAs(res.data, `Library-Insights_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch { toast.error('Could not export the PDF'); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => {
    let dead = false;
    axios.get(`${API_URL}/api/rfp/boilerplate/insights/dashboard`, { withCredentials: true })
      .then(res => { if (!dead) setData(res.data); })
      .catch(() => { if (!dead) setData(null); })
      .finally(() => { if (!dead) setLoading(false); });
    axios.get(`${API_URL}/api/rfp/library-backups`, { withCredentials: true })
      .then(res => { if (!dead) setBackups(res.data.backups || []); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" /></div>;
  if (!data) return <p className="text-sm text-slate-400 py-10 text-center" data-testid="lib-dash-error">Could not load Library insights.</p>;

  const freshTotal = Math.max(1, Object.values(data.freshness).reduce((a, b) => a + b, 0));
  const maxCat = Math.max(1, ...data.by_category.map(c => c.count));
  const maxTop = Math.max(1, ...data.top_used.map(t => t.usage));
  const maxWeek = Math.max(1, ...data.usage_weeks.map(w => w.events));
  const maxScore = 100;

  return (
    <div className="space-y-4" data-testid="library-insights-dashboard">
      <div className="flex justify-end">
        <Button size="sm" className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" onClick={exportPdf} disabled={pdfBusy}
          data-testid="lib-dash-pdf-btn">
          {pdfBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}Export PDF
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi label="Active entries" value={data.total}
          sub={`${data.new_90d} new <90d · ${data.archived} archived`} testId="lib-dash-kpi-total" />
        <Kpi label="Library health" value={`${data.health.score} · ${data.health.label}`}
          sub={`${data.health.dup_pairs} possible duplicate pair${data.health.dup_pairs === 1 ? '' : 's'}`} testId="lib-dash-kpi-health" />
        <Kpi label="Stale >12 months" value={data.health.stale} sub="need a re-check" testId="lib-dash-kpi-stale" />
        <Kpi label="Never used" value={data.never_used} sub="click to review — improve or retire"
          onClick={() => setNeverOpen(true)} testId="lib-dash-kpi-neverused" />
        <Kpi label="Pending cleanups" value={data.health.pending_recs} sub={`${data.cleanup.sessions} review sessions run`} testId="lib-dash-kpi-pending" />
      </div>
      <Dialog open={neverOpen} onOpenChange={setNeverOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="lib-dash-neverused-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F2040]">Never-used entries — {data.never_used}</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-slate-500">No copies, includes or RFP matches ever recorded. Improve the wording/keywords so they match, or retire them via Clean &amp; Consolidate.</p>
          {(data.never_used_entries || []).map(e => (
            <div key={e.capability_id} className="flex items-center gap-2 text-xs border-b border-slate-100 py-1.5"
              data-testid={`lib-dash-never-${e.capability_id}`}>
              <span className="w-20 font-mono text-[10px] text-slate-400 shrink-0">{e.capability_id}</span>
              <span className="flex-1 truncate text-slate-700">{e.title}</span>
              <Badge variant="outline" className="text-[9px] shrink-0">{e.category}</Badge>
              <span className="text-[9px] text-slate-400 shrink-0">upd {(e.updated_at || '').slice(0, 10)}</span>
              <button onClick={() => boostKeywords(e)} disabled={rowBusy !== ''}
                className="text-[#4ABFCF] hover:text-[#0F8A9E] shrink-0 disabled:opacity-40"
                title="AI-refresh this entry's keywords so it starts matching RFP questions"
                data-testid={`lib-dash-never-boost-${e.capability_id}`}>
                {rowBusy === `boost-${e.capability_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
              </button>
              <button onClick={() => retireEntry(e)} disabled={rowBusy !== ''}
                className="text-slate-400 hover:text-rose-600 shrink-0 disabled:opacity-40"
                title="Retire (archive) this entry — reversible from the library"
                data-testid={`lib-dash-never-retire-${e.capability_id}`}>
                {rowBusy === `retire-${e.capability_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
              </button>
              <button onClick={() => { setNeverOpen(false); navigate(`/rfp-library?category=${encodeURIComponent(e.category)}`); }}
                className="text-[#4ABFCF] shrink-0" title="Open in library" data-testid={`lib-dash-never-open-${e.capability_id}`}>
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          ))}
          {data.never_used > (data.never_used_entries || []).length && (
            <p className="text-[10px] text-slate-400">Showing the first {(data.never_used_entries || []).length} of {data.never_used}.</p>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="lib-dash-freshness">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <Layers className="w-4 h-4 text-[#4ABFCF]" />Freshness mix</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="h-4 rounded-full overflow-hidden flex bg-slate-100">
              {FRESH_META.map(([k, , cls]) => (
                <div key={k} className={cls} style={{ width: `${(data.freshness[k] / freshTotal) * 100}%` }} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-1">
              {FRESH_META.map(([k, label, cls]) => (
                <p key={k} className="text-[10px] text-slate-500 flex items-center gap-1.5" data-testid={`lib-dash-fresh-${k}`}>
                  <span className={`w-2 h-2 rounded-full ${cls}`} />{label}
                  <span className="font-semibold text-[#0F2040]">{data.freshness[k]}</span>
                  <span className="text-slate-400">({Math.round((data.freshness[k] / freshTotal) * 100)}%)</span>
                </p>
              ))}
            </div>
            <p className="text-[10px] text-slate-400">Based on each entry's last update — stale entries are the top health penalty.</p>
          </CardContent>
        </Card>

        <Card data-testid="lib-dash-health-trend">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <HeartPulse className="w-4 h-4 text-[#4ABFCF]" />Health score trend</CardTitle></CardHeader>
          <CardContent>
            {data.health_trend.length === 0 ? <p className="text-xs text-slate-400">No snapshots yet.</p> : (
              <div className="flex items-end gap-1 h-24">
                {data.health_trend.map(s => (
                  <div key={s.day} className="flex-1 flex flex-col items-center gap-0.5" title={`${s.day}: ${s.score}`}>
                    <span className="text-[8px] text-slate-400">{s.score}</span>
                    <div className={`w-full rounded-t ${s.score >= 80 ? 'bg-emerald-400' : s.score >= 60 ? 'bg-amber-400' : 'bg-rose-400'}`}
                      style={{ height: `${(s.score / maxScore) * 72}px` }} />
                    <span className="text-[7px] text-slate-400">{s.day.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="lib-dash-usage">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <Activity className="w-4 h-4 text-[#4ABFCF]" />Usage — last 8 weeks</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.usage_weeks.length === 0 ? <p className="text-xs text-slate-400">No copy/include events in the window.</p> : (
              <div className="flex items-end gap-1.5 h-20">
                {data.usage_weeks.map(w => (
                  <div key={w.week} className="flex-1 flex flex-col items-center gap-0.5" title={`${w.week}: ${w.events} events`}>
                    <span className="text-[8px] text-slate-400">{w.events}</span>
                    <div className="w-full rounded-t bg-[#4ABFCF]" style={{ height: `${(w.events / maxWeek) * 56}px` }} />
                    <span className="text-[7px] text-slate-400">{w.week.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="pt-1 border-t border-slate-100 space-y-1">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Most used entries (lifetime + RFP matches)</p>
              {data.top_used.map(t => (
                <div key={t.capability_id} className="flex items-center gap-2 text-[10px] text-slate-500" data-testid={`lib-dash-top-${t.capability_id}`}>
                  <span className="w-16 font-mono text-slate-400">{t.capability_id}</span>
                  <span className="w-44 truncate text-slate-600">{t.title}</span>
                  <Bar pct={(t.usage / maxTop) * 100} />
                  <span className="w-10 text-right font-semibold text-[#0F2040]">{t.usage}×</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="lib-dash-categories">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <BookOpen className="w-4 h-4 text-[#4ABFCF]" />Entries by category</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {data.by_category.map(c => (
              <button key={c.category} onClick={() => navigate(`/rfp-library?category=${encodeURIComponent(c.category)}`)}
                className="w-full flex items-center gap-2 text-xs hover:bg-slate-50 rounded px-0.5 text-left transition-colors"
                title={`Open the library filtered to ${c.category}`} data-testid={`lib-dash-cat-${c.category}`}>
                <span className="w-40 truncate text-slate-600 hover:text-[#0F8A9E]">{c.category}</span>
                <Bar pct={(c.count / maxCat) * 100} />
                <span className="w-28 text-[10px] text-slate-400 text-right">{c.count} entries · {c.usage}× used</span>
              </button>
            ))}
            <div className="pt-2 border-t border-slate-100 flex items-center gap-2 text-[10px] text-slate-500" data-testid="lib-dash-cleanup">
              <Sparkles className="w-3 h-3 text-[#4ABFCF]" />
              Last cleanup review: {data.cleanup.last_review_at ? data.cleanup.last_review_at.slice(0, 10) : 'never'}
              {data.cleanup.last_status && <Badge variant="outline" className="text-[8px]">{data.cleanup.last_status}</Badge>}
              <span className="flex-1" />
              <span>{data.cleanup.pending} pending · {data.cleanup.accepted} accepted · {data.cleanup.dismissed} dismissed</span>
            </div>
            {(data.recently_archived || []).length > 0 && (
              <div className="pt-2 border-t border-slate-100 space-y-1" data-testid="lib-dash-recently-archived">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Recently retired — one-click restore</p>
                {data.recently_archived.map(e => (
                  <div key={e.id} className="flex items-center gap-2 text-[10px] text-slate-500"
                    data-testid={`lib-dash-archived-${e.capability_id}`}>
                    <span className="w-16 font-mono text-slate-400 shrink-0">{e.capability_id}</span>
                    <span className="flex-1 truncate text-slate-600">{e.title}</span>
                    <span className="text-slate-400 shrink-0">{(e.archived_at || '').slice(0, 10)}{e.archived_by ? ` by ${e.archived_by.split('@')[0]}` : ''}</span>
                    <button onClick={() => restoreEntry(e)} disabled={rowBusy !== ''}
                      className="text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5 font-semibold shrink-0 disabled:opacity-40"
                      title="Restore this entry to the active library"
                      data-testid={`lib-dash-restore-${e.capability_id}`}>
                      {rowBusy === `restore-${e.capability_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
            {recipients !== null && (
              <div className="pt-2 border-t border-slate-100 flex items-center gap-2" data-testid="lib-dash-recipients">
                <Mail className="w-3 h-3 text-[#4ABFCF] shrink-0" />
                <span className="text-[10px] text-slate-500 shrink-0" title="Admins always receive the monthly report; add extra recipients here">Monthly report also to</span>
                <input value={recipients} onChange={e => setRecipients(e.target.value)}
                  placeholder="extra emails, comma-separated"
                  className="flex-1 h-6 text-[10px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                  data-testid="lib-dash-recipients-input" />
                <button onClick={saveRecipients}
                  className="text-[10px] font-semibold text-[#4ABFCF] hover:underline shrink-0"
                  data-testid="lib-dash-recipients-save">Save</button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="lib-dash-backups">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between text-[#0F2040]">
            <span className="flex items-center gap-2"><Archive className="w-4 h-4 text-[#4ABFCF]" />Library Backups — one-click restore</span>
            <button onClick={backupNow} disabled={bkBusy !== ''}
              className="text-[10px] font-semibold text-[#4ABFCF] hover:underline flex items-center gap-1 disabled:opacity-40"
              data-testid="lib-backup-now-btn">
              {bkBusy === 'now' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}Back up now
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {(backups || []).length === 0 && (
            <p className="text-[11px] text-slate-400" data-testid="lib-backups-empty">
              No snapshots yet — one is taken automatically every night, or click "Back up now".
            </p>
          )}
          {(backups || []).map(b => (
            <div key={b.id} className="flex items-center gap-2 text-[11px] text-slate-500 border-b border-slate-100 py-1"
              data-testid={`lib-backup-row-${b.id}`}>
              <span className="w-20 font-mono text-slate-400 shrink-0">{b.day}</span>
              <span className="w-14 shrink-0">{(b.at || '').slice(11, 16)} UTC</span>
              <Badge variant="outline" className="text-[8px] shrink-0">{b.reason}</Badge>
              <span className="flex-1 truncate text-slate-600">
                {b.block_count} entries · {b.category_count} categories{b.by && b.by !== 'system' ? ` · by ${b.by.split('@')[0]}` : ''}
              </span>
              <button onClick={() => restoreBackup(b)} disabled={bkBusy !== ''}
                className="text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5 font-semibold shrink-0 disabled:opacity-40"
                title="Restore the whole library to this snapshot (current state is backed up first)"
                data-testid={`lib-backup-restore-${b.id}`}>
                {bkBusy === `restore-${b.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}Restore
              </button>
            </div>
          ))}
          <p className="text-[9px] text-slate-400 pt-1">Nightly snapshots are kept for the last 14. A restore first snapshots the current state, so it can itself be undone.</p>
        </CardContent>
      </Card>
    </div>
  );
};
